import React, { useState, useEffect, useMemo } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../firebase.js";
import * as utils from "./Utils.js";

/* ============================
   CONSTANTS
============================ */
const dietaryRestrictionsIgnoreList = [
  "no",
  "n/a",
  "na",
  "",
  "none",
  "no restrictions",
  "no!"
];

const hasDietaryRestrictions = (restriction) => {
  const r = restriction.toLowerCase().trim();
  return !dietaryRestrictionsIgnoreList.includes(r);
}

const mealToEmojiMap = {
  Filet: "🥩",
  Salmon: "🐟",
  Rigatoni: "🍝",
  "Dry Aged New York Strip Steak": "🥩",
  "Herb Roasted Murray's Statler Chicken Breast": "🍗",
  "Summer Vegetable Rigatoni": "🍝",
};

const eventEmojiMap = {
  "Bridal Party Brunch": "🥐",
  "Ceremony Rehearsal": "⛪️",
  "Rehearsal Dinner": "💚💛",
  "Welcome Party": "🍸🎉",
  "Reception": "💍🪩"
};

const AdminRsvps = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [guestsById, setGuestsById] = useState({});
  const [events, setEvents] = useState([]);
  const [reservationPageViews, setReservationPageViews] = useState({});
  const [rsvpLogs, setRsvpLogs] = useState([]);
  const [visibleLogCount, setVisibleLogCount] = useState(2);

  /* ============================
     AUTH + DATA FETCH
  ============================ */

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) {
        window.location.href = "/login";
        return;
      }
      setCurrentUser(user);
      fetchAll();
    });
    return () => unsub();
  }, []);

  const fetchAll = async () => {
    const logsQuery = query(
      collection(db, "rsvp_logs"),
      orderBy("submitted_at", "desc")
    );

    const [
      resSnap,
      guestSnap,
      eventSnap,
      pageViewSnap,
      logsSnap,
    ] = await Promise.all([
      getDocs(collection(db, "reservations")),
      getDocs(collection(db, "guests")),
      getDocs(collection(db, "events")),
      getDocs(collection(db, "rsvp_page_views")),
      getDocs(logsQuery),
    ]);

    setReservations(resSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

    const guests = {};
    guestSnap.docs.forEach((d) => {
      guests[d.id] = d.data();
    });
    setGuestsById(guests);

    setEvents(
      eventSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ad = a.start?.toDate ? a.start.toDate() : new Date(a.start);
          const bd = b.start?.toDate ? b.start.toDate() : new Date(b.start);
          return ad - bd;
        })
    );

    const pageViewMap = {};
    pageViewSnap.docs.forEach((d) => {
      const data = d.data();
      const reservationId = data.reservation_id;

      if (!pageViewMap[reservationId]) {
        pageViewMap[reservationId] = [];
      }

      pageViewMap[reservationId].push({
        id: d.id,
        ...data
      });
    });
    setReservationPageViews(pageViewMap);

    setRsvpLogs(
      logsSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }))
    );
  };

  /* ============================
    GROUP RSVP LOGS BY
    reservation_id + submitted_at
  ============================ */

  const groupedRsvpLogs = useMemo(() => {
    const groups = {};

    rsvpLogs.forEach((log) => {
      const ms = log.submitted_at?.toDate?.().getTime() || 0;

      const minute = Math.floor(ms / (60 * 1000));

      const key = `${log.reservation_id}_${minute}`;

      if (!groups[key]) {
        groups[key] = {
          reservation_id: log.reservation_id,
          submitted_at: log.submitted_at,
          guest_entries: [],
          log_ids: [],
        };
      }

      groups[key].guest_entries.push(log);
      groups[key].log_ids.push(log.id);
    });

    return Object.values(groups).sort((a, b) => {
      const aTime = a.submitted_at?.toDate?.().getTime() || 0;
      const bTime = b.submitted_at?.toDate?.().getTime() || 0;
      return bTime - aTime;
    });
  }, [rsvpLogs]);

  /* ============================
   LATEST LOG MAP (per reservation)
  ============================ */

  const latestLogMap = useMemo(() => {
    const map = {};

    groupedRsvpLogs.forEach((group) => {
      const existing = map[group.reservation_id];

      const currentTime =
        group.submitted_at?.toDate?.().getTime() || 0;

      if (!existing) {
        map[group.reservation_id] = group;
        return;
      }

      const existingTime =
        existing.submitted_at?.toDate?.().getTime() || 0;

      if (currentTime > existingTime) {
        map[group.reservation_id] = group;
      }
    });

    return map;
  }, [groupedRsvpLogs]);

  /* ============================
     EVENT STATS
  ============================ */

  const getEventStats = (evt) => {
    let invited = 0,
      yes = 0,
      no = 0,
      pending = 0;
    const mealCounts = {};

    Object.values(guestsById).forEach((guest) => {
      const ge = guest.events?.find((e) => e.id === evt.id);
      if (!ge) return;

      invited++;

      if (ge.rsvp === "yes") {
        yes++;
        if (evt.rsvpType === "meal" && ge.meal_id) {
          mealCounts[ge.meal_id] = (mealCounts[ge.meal_id] || 0) + 1;
        }
      } else if (ge.rsvp === "no") no++;
      else pending++;
    });

    const responded = yes + no;

    return {
      invited,
      yes,
      no,
      pending,
      percentResponded: invited
        ? Math.round((responded / invited) * 100)
        : 0,
      percentYes: responded ? Math.round((yes / responded) * 100) : 0,
      mealCounts,
    };
  };

  /* ============================
     GROUPING
  ============================ */

  const getReservationInviteCount = (reservation) => {
    let max = 0;
    (reservation.guests || []).forEach((guestId) => {
      const guest = guestsById[guestId];
      if (!guest) return;
      max = Math.max(max, guest.events?.length || 0);
    });
    return max;
  };

  const groupReservations = () => {
    const groups = { 5: [], 4: [], 3: [], 2: [] };
    reservations.forEach((res) => {
      const count = getReservationInviteCount(res);
      if (groups[count]) groups[count].push(res);
    });
    return groups;
  };

  const getEventsForGroup = (groupReservations) => {
    const ids = new Set();
    groupReservations.forEach((res) =>
      (res.guests || []).forEach((gid) =>
        guestsById[gid]?.events?.forEach((e) => ids.add(e.id))
      )
    );
    return events.filter((e) => ids.has(e.id));
  };

  const getReservationRsvpStatus = (reservation, groupEvents) => {
    let hasPending = false;
    let hasYes = false;
    let hasNo = false;

    (reservation.guests || []).forEach((gid) => {
      const guest = guestsById[gid];
      if (!guest) return;

      groupEvents.forEach((evt) => {
        const ge = guest.events?.find((e) => e.id === evt.id);
        if (!ge) return;

        if (ge.rsvp === "yes") hasYes = true;
        else if (ge.rsvp === "no") hasNo = true;
        else hasPending = true;
      });
    });

    if (hasPending) return 3;
    if (hasNo && !hasYes) return 2;
    return 1;
  };

  const sortGroupReservations = (groupReservations, groupEvents) => {
    return [...groupReservations].sort((a, b) => {
      const statusDiff =
        getReservationRsvpStatus(a, groupEvents) -
        getReservationRsvpStatus(b, groupEvents);

      if (statusDiff !== 0) return statusDiff;

      const aTime =
        latestLogMap[a.id]?.submitted_at?.toDate?.().getTime() || 0;
      const bTime =
        latestLogMap[b.id]?.submitted_at?.toDate?.().getTime() || 0;

      return bTime - aTime;
    });
  };

  /* ============================
     RSVP CELL
  ============================ */

  const renderRsvpCell = (guest, evt) => {
    const ge = guest.events?.find((e) => e.id === evt.id);
    if (!ge) return "";

    if (ge.rsvp === "yes") {
      if (evt.rsvpType === "meal" && ge.meal_id) {
        const meal = evt.mealOptions?.find(
          (m) => m.id === ge.meal_id
        );
        return meal ? `✅ ${mealToEmojiMap[meal.name]}` : "✅";
      }
      return "✅";
    }

    if (ge.rsvp === "no") return "❌";

    return "⚠️";
  };

  /* ============================
    CSV DOWNLOAD
  ============================ */

  const downloadGuestCsv = () => {
    const normalizeDietary = (val) => {
      if (!val || !hasDietaryRestrictions(val)) {
        return "";
      }
      return val;
    };

    /* ============================
      BUILD RSVP TIME MAP
    ============================ */

    const reservationRsvpTimes = {};

    rsvpLogs.forEach((log) => {
      const time = log.submitted_at?.toDate?.().getTime();
      if (!time) return;

      const existing = reservationRsvpTimes[log.reservation_id];

      if (!existing || time > existing) {
        reservationRsvpTimes[log.reservation_id] = time;
      }
    });

    const rows = [];

    const header = [
      "Reservation ID",
      "RSVP Time",
      "Name",
      "Over 21",
      "Type",
      "Dietary Restrictions",

      // 👇 Lodging fields
      "Lodging ID",
      "Lodging Name",
      "Lodging Address",

      ...events.flatMap((e) => {
        const cols = [`${e.name} RSVP`];

        if (e.rsvpType === "meal") {
          cols.push(`${e.name} Meal`);
        }

        return cols;
      }),
    ];

    rows.push(header);

    reservations.forEach((res) => {
      const rsvpTime = reservationRsvpTimes[res.id]
        ? new Date(reservationRsvpTimes[res.id]).toLocaleString()
        : "";

      const lodging = res.lodging || {};

      (res.guests || []).forEach((gid) => {
        const guest = guestsById[gid];
        if (!guest) return;

        const attending = guest.events?.some((e) => e.rsvp === "yes");
        if (!attending) return;

        const type = guest.type === "child" ? "Child" : "Adult";

        const row = [
          res.id,
          rsvpTime,
          guest.name,
          guest.over21 ? "Yes" : "No",
          type,
          normalizeDietary(guest.dietary_restrictions),

          // 👇 Lodging values
          lodging.id || "",
          lodging.name || "",
          lodging.address || "",
        ];

        events.forEach((evt) => {
          const ge = guest.events?.find((e) => e.id === evt.id);

          if (!ge) {
            row.push("");
            if (evt.rsvpType === "meal") row.push("");
            return;
          }

          const rsvp =
            ge.rsvp === "yes"
              ? "Yes"
              : ge.rsvp === "no"
              ? "No"
              : "";

          row.push(rsvp);

          if (evt.rsvpType === "meal") {
            if (!ge.meal_id) {
              row.push("");
            } else {
              const meal = evt.mealOptions?.find((m) => m.id === ge.meal_id);
              row.push(meal?.name || ge.meal_id);
            }
          }
        });

        rows.push(row);
      });
    });

    /* ============================
      SORT ROWS
      blank RSVP times first,
      then earliest → latest
      ============================ */

    const headerRow = rows[0];
    const dataRows = rows.slice(1);

    dataRows.sort((a, b) => {
      const aTime = a[1];
      const bTime = b[1];

      if (!aTime && !bTime) return 0;
      if (!aTime) return -1;
      if (!bTime) return 1;

      return new Date(aTime) - new Date(bTime);
    });

    const sortedRows = [headerRow, ...dataRows];

    const csv = sortedRows
      .map((row) =>
        row
          .map((val) => `"${String(val ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");

    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "guest_rsvps.csv";
    a.click();

    URL.revokeObjectURL(url);
  };

  /* ============================
     RENDER
  ============================ */

  const groups = groupReservations();
  const visibleGroups = useMemo(() => {
    return groupedRsvpLogs.slice(0, visibleLogCount);
  }, [groupedRsvpLogs, visibleLogCount]);

  return (
    <div className="AdminRsvps">
      <div className="centered-box wedding-page-block">
        <div className="wedding-centerpiece">
          <p style={{ margin: "15px 0 0 0", width: "100%" }}>
            <a
              style={{ textDecoration: "none", color: "#00233f" }}
              href="/admin"
            >
              <b>← Back to Admin</b>
            </a>
          </p>
          <p className="wedding-page-title">RSVPs</p>
          <div style={{ marginBottom: "15px" }}>
            <button
              className="btn btn-sm btn-dark"
              onClick={downloadGuestCsv}
            >
              Download Guest RSVPs
            </button>
          </div>
        </div>

        {/* EVENT STATS */}
        {events.map((evt) => {
          const stats = getEventStats(evt);

          return (
            <div key={evt.id} className="mb-4">
              <h5>{evt.name}</h5>

              <div className="row mb-2">
                {[
                  ["Invited", stats.invited],
                  ["Yes", stats.yes],
                  ["No", stats.no],
                  ["Pending", stats.pending],
                  ["% RSVP", `${stats.percentResponded}%`],
                  ["% Yes", `${stats.percentYes}%`],
                ].map(([label, val]) => (
                  <div key={label} className="col-6 col-md-2 mb-2">
                    <div className="card text-center shadow-sm">
                      <div className="card-body py-2">
                        <div className="fw-bold">{val}</div>
                        <small>{label}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        <hr className="my-4" />

        {/* RECENT ACTIVITY */}
        {groupedRsvpLogs.length > 0 && (
          <div className="mb-5">
            <h4 className="mb-3">Recent RSVP Activity</h4>

            <div className="card shadow-sm">
              <div className="card-body">

                {groupedRsvpLogs.length === 0 ? (
                  <div className="text-muted">No activity yet.</div>
                ) : (
                  visibleGroups.map((group) => {
                    const reservation = reservations.find(
                      (r) => r.id === group.reservation_id
                    );

                    const formattedTime =
                      group.submitted_at?.toDate?.().toLocaleString() || "";

                    const timeKey =
                      group.submitted_at?.toDate?.().getTime() || 0;

                    return (
                      <div
                        key={`${group.reservation_id}_${timeKey}`}
                        className="mb-4 pb-3 border-bottom"
                      >

                        {/* Log IDs */}
                        <div>
                          {group.log_ids.map((id) => (
                            <code key={id} style={{ marginRight: "6px" }}>
                              {id}
                            </code>
                          ))}
                        </div>

                        {/* Reservation Header */}
                        <div className="d-flex justify-content-between">
                          <div>
                            <code>{group.reservation_id}</code>
                          </div>
                        </div>

                        <div className="d-flex justify-content-between">
                          <div>
                            <strong><em>
                              {reservation?.name || group.reservation_id}
                            </em></strong>
                          </div>
                          <small className="text-muted">
                            {formattedTime}
                          </small>
                        </div>

                        {/* LOOP THROUGH EACH GUEST ENTRY */}
                        {group.guest_entries.map((log) => {

                          let lodgingUpdate = null;

                          if (typeof log.diff === "string") {
                            try {
                              const parsed = JSON.parse(log.diff);
                              if (parsed && typeof parsed === "object" && "name" in parsed) {
                                lodgingUpdate = parsed;
                              }
                            } catch {}
                          }

                          return (
                            <div key={log.id} className="mt-2 ms-2">

                              <div className="fw-semibold">
                                {log.guest_name}
                              </div>

                              {/* EVENTS */}
                              {log.diff?.events?.map((evtChange, idx) => {
                                const evt = events.find(
                                  (e) => e.id === evtChange.event_id
                                );

                                return (
                                  <div key={idx} className="ms-3">

                                    <div className="fw-semibold">
                                      {evt?.name || evtChange.event_id}
                                    </div>

                                    {evtChange.changes?.rsvp && (
                                      <div className="text-muted ms-2">
                                        RSVP:{" "}
                                        <span style={{ color: "#a94442" }}>
                                          {evtChange.changes.rsvp.from || "—"}
                                        </span>{" "}
                                        →
                                        <span style={{ color: "#3c763d" }}>
                                          {evtChange.changes.rsvp.to}
                                        </span>
                                      </div>
                                    )}

                                    {evtChange.changes?.meal_id && (
                                      <div className="text-muted ms-2">
                                        Meal:{" "}
                                        <span style={{ color: "#a94442" }}>
                                          {evtChange.changes.meal_id.from || "—"}
                                        </span>{" "}
                                        →
                                        <span style={{ color: "#3c763d" }}>
                                          {evtChange.changes.meal_id.to}
                                        </span>
                                      </div>
                                    )}

                                  </div>
                                );
                              })}

                              {/* LODGING */}
                              {lodgingUpdate && (
                                <div className="ms-3 text-muted">
                                  {lodgingUpdate.id === "hotel" ? (
                                    <>🏨 {lodgingUpdate.name} - {lodgingUpdate.address}</>
                                  ) : lodgingUpdate.id === "rental" ? (
                                    <>🏠 {lodgingUpdate.name} - {lodgingUpdate.address}</>
                                  ) : lodgingUpdate.id === "other" ? (
                                    <>🛖 {lodgingUpdate.name} - {lodgingUpdate.address}</>
                                  ) : (
                                    <>⚠️ Not Booked Yet</>
                                  )}
                                </div>
                              )}

                              {/* DIETARY */}
                              {log.diff?.dietary_restrictions && (
                                <div className="ms-3 text-muted">
                                  Dietary:{" "}
                                  <span style={{ color: "#a94442" }}>
                                    {log.diff.dietary_restrictions.from || "—"}
                                  </span>{" "}
                                  →
                                  <span style={{ color: "#3c763d" }}>
                                    {log.diff.dietary_restrictions.to}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}

                      </div>
                    );
                  })
                )}

                {groupedRsvpLogs.length > 2 && (
                  <div className="mt-3">
                    <button
                      className="btn btn-sm btn-outline-dark"
                      onClick={() => setVisibleLogCount((prev) => prev + 2)}
                      style={{marginRight: '10px'}}
                    >
                      Show More
                    </button>
                    {visibleLogCount !== 2 ? (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setVisibleLogCount(2)}
                      >
                        Collapse
                      </button>
                    ) : <></>}
                  </div>
                )}

              </div>
            </div>
          </div>
        )}

        {/* GROUP TABLES */}
        {Object.entries(groups)
          .sort(([a], [b]) => b - a)
          .map(([count, groupReservations]) => {
            if (!groupReservations.length) return null;

            const groupEvents = getEventsForGroup(groupReservations);
            const sortedReservations = sortGroupReservations(
              groupReservations,
              groupEvents
            );

            return (
              <div key={count} className="mb-5">
                <h4>
                  Invited to {count} Event{count > 1 ? "s" : ""}
                </h4>

                <div className="table-responsive">
                  <table className="table table-sm table-bordered align-middle text-center">
                    <thead>
                      <tr>
                        <th className="text-start">Guest</th>
                        {groupEvents.map((evt) => (
                          <th key={evt.id}>
                            <span className="d-none d-md-inline">
                              {evt.name}
                            </span>
                            <span className="d-inline d-md-none">
                              {eventEmojiMap[evt.name] || "📅"}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedReservations.map((res) => (
                        <React.Fragment key={res.id}>
                          <tr className="table-light">
                            <td
                              colSpan={groupEvents.length + 1}
                              className="fw-bold text-start"
                            >
                              <a
                                target="_blank"
                                rel="noreferrer"
                                href={`https://chandlerandanna.com/rsvp/${res.id}`}
                              >
                                Link to RSVP
                              </a>{" "}
                              <code>{res.id}</code> {res.name}
                              {reservationPageViews?.[res.id]?.length
                                ?
                                <>
                                  <span> 👀 </span>
                                  <code>{reservationPageViews[res.id].map(pv => pv.id.substring(0, 6)).join(', ')}</code>
                                </>
                                : ""}
                            </td>
                          </tr>

                          {(res.guests || []).map((gid) => {
                            const guest = guestsById[gid];
                            if (!guest) return null;

                            return (
                              <React.Fragment key={gid}>
                                <tr>
                                  <td className="text-start">
                                    <code>{gid}</code> {guest.name}{" "}
                                  </td>
                                  {groupEvents.map((evt) => (
                                    <td key={evt.id}>
                                      {renderRsvpCell(guest, evt)}
                                    </td>
                                  ))}
                                </tr>

                                {guest.dietary_restrictions &&
                                  hasDietaryRestrictions(guest.dietary_restrictions) && (
                                    <tr>
                                      <td
                                        colSpan={
                                          groupEvents.length + 1
                                        }
                                        className="text-start"
                                      >
                                        ‼️ Restriction:{" "}
                                        {
                                          guest.dietary_restrictions
                                        }
                                      </td>
                                    </tr>
                                  )}
                              </React.Fragment>
                            );
                          })}

                          { res.guests.some((gid) => guestsById[gid].events.some((evt) => evt.rsvp === "yes")) ?
                            <tr className="">
                              <td colSpan={groupEvents.length + 1} className="text-start">
                                { 
                                  res?.lodging?.id === 'hotel' ? `🏨 ${res?.lodging?.name} - ${res?.lodging?.address}` :
                                  res?.lodging?.id === 'rental' ?  `🏠 ${res?.lodging?.name} - ${res?.lodging?.address}` :
                                  res?.lodging?.id === 'other' ?  `🛖 ${res?.lodging?.name} - ${res?.lodging?.address}` : `⚠️ Not Booked Yet`
                                }
                              </td>
                            </tr> : <></>
                          }
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
      </div>

      <div className="centered-box">
        {utils.displayCheckerboard(20, 42, ["black", "ivory"])}
      </div>
    </div>
  );
};

export default AdminRsvps;
